import React, { useState } from 'react';
import { Customer } from '../../graphql/entities';
import DateField from '../connectedForm/DateInput/DateInput';
import Dropdown from '../../components/connectedForm/Dropdown';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import Label from '../connectedForm/Label/Label';
import { default as Form } from '../connectedForm';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import periodQuery from '../../graphql/queries/company/FinancialPeriodsQuery.graphql';
import {
  FilterOperation,
  IFilterInput,
  IFinancialPeriod,
  IGenerateSalesLedgerDayBookReportMutationResult,
  IGenerateSalesLedgerDayBookReportMutationVariables,
  IReportTemplate,
  ReportOutputType,
  ReportType,
} from '../../graphql/schema/p4types';
import GenerateSalesLedgerDayBookReportMutation from '../../graphql/mutations/customer/GenerateSalesLedgerDayBookReportMutation.graphql';
import { ModalBottomButtonContainer } from './Styles';
import { Button, SuccessButton } from '../connectedForm/Button';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalRow } from '../connectedForm/Row';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useReportTemplatesQuery } from '../../utils/hooks';

import { BasicOutputSelection } from './BasicOutputSelection';
import FluentModal from './FluentModal';

const financialPeriodCustomVariables = (): {
  first: number;
  orderBy: string;
  orderDirection: string;
  filters: Array<IFilterInput>;
} => {
  return {
    first: 100,
    orderBy: 'startDate',
    orderDirection: 'DESC',
    filters: [
      {
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'customerPostings',
          value: 'FUTURE',
        },
      },
    ],
  };
};

const SalesLedgerDayBookCriteriaModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [customer, setCustomer] = useState();
  const [template, setTemplate] = useState<Maybe<IReportTemplate>>();
  const [outputType, setOutputType] = useState<Maybe<{ value: ReportOutputType; label: string }>>();
  const [documentStartDate, setDocumentStartDate] = useState();
  const [documentFinishDate, setDocumentFinishDate] = useState();
  const [postingStartDate, setPostingStartDate] = useState();
  const [postingFinishDate, setPostingFinishDate] = useState();
  const [documentTypes, setDocumentTypes] = useState<Maybe<Array<any>>>();
  const [financialPeriods, setFinancialPeriods] = useState<Array<IFinancialPeriod>>([]);

  const onChangePeriods = (values: Array<IFinancialPeriod>) => {
    const selectedValues = values?.filter((value) => !value.deleted) ?? [];
    setFinancialPeriods(selectedValues);
  };

  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const [generateDaybook] = useBackgroundMutation<
    IGenerateSalesLedgerDayBookReportMutationResult,
    IGenerateSalesLedgerDayBookReportMutationVariables
  >(GenerateSalesLedgerDayBookReportMutation, {
    onCompleteJob: (job) => {
      if (job.properties.reportFile) {
        const reportFileName: string = job.properties.reportFile;
        const newTab = checkPopUpBlockerFunction(`/api/resources/report/${reportFileName}`, '_blank');
        //An XLSX will download, rather than print.
        if (!reportFileName.endsWith('xlsx')) {
          newTab?.print();
        }
      }
      onClose();
    },
  });

  const { templates } = useReportTemplatesQuery(ReportType.DayBookReport);

  const onPrint = () => {
    generateDaybook({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          reportParameters: {
            template: normalizeVariables(template ?? null),
            outputType: outputType?.value ?? null,
          },
          dayBookReportCriteria: normalizeVariables({
            customer: customer,
            documentStartDate: documentStartDate,
            documentFinishDate: documentFinishDate,
            postingStartDate: postingStartDate,
            postingFinishDate: postingFinishDate,
            // the default documentType is include all sales types
            documentTypes: documentTypes
              ? documentTypes.map((documentType) => documentType.value)
              : fieldMetaData.SalesDocumentType.enumValues.map((documentType) => documentType.value),
            financialPeriods: financialPeriods,
          }),
        },
      },
    });
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { paddingBottom: '60px', minWidth: '700px', maxWidth: '1000px' } }}>
      <BasicHeader>Sales Daybook</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Dropdown
          data-testid="sales-ledger-daybook-template"
          options={templates}
          valueKey="id"
          labelKey="name"
          onChange={(newTemplate: IReportTemplate) => {
            setTemplate(newTemplate);
            setOutputType(getDefaultOutputType(newTemplate?.reportTemplateFormat));
          }}
          label="Template"
          value={template}
          inputCols={8 / 12}
          labelCols={4 / 12}
        />
        <Dropdown
          data-testid="sales-ledger-daybook-output"
          options={BasicOutputSelection}
          label="Output"
          onChange={setOutputType}
          value={outputType}
          valueKey="value"
          labelKey="label"
          clearable={false}
          inputCols={8 / 12}
          labelCols={4 / 12}
        />
        <QueryDropdown
          data-testid="sales-ledger-daybook-customer"
          label="Customer"
          {...Customer.dropdown}
          selectModal={null}
          inputCols={8 / 12}
          labelCols={4 / 12}
          value={customer}
          onChange={setCustomer}
        />
        <Dropdown
          data-testid="sales-ledger-daybook-document-types"
          options={fieldMetaData.SalesDocumentType.enumValues}
          label="Source Type"
          placeholder="Search source types"
          value={documentTypes}
          multi={true}
          valueKey="value"
          labelKey="label"
          onChange={setDocumentTypes}
          inputCols={8 / 12}
          labelCols={4 / 12}
        />
        <QueryDropdown
          data-testid="sales-ledger-daybook-financial-periods"
          label="Financial Period"
          query={periodQuery}
          dropdown={{ customVariables: financialPeriodCustomVariables }}
          value={financialPeriods}
          valueKey="id"
          labelKey="name"
          placeholder="Search financial periods"
          multi={true}
          clearable={true}
          onChange={onChangePeriods}
          inputCols={8 / 12}
          labelCols={4 / 12}
        />
        <Label>Document Dates</Label>
        <ModalRow>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="sales-ledger-daybook-document-start-date"
              label="Start"
              portal={true}
              value={documentStartDate}
              onChange={setDocumentStartDate}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="sales-ledger-daybook-document-finish-date"
              label="Finish"
              portal={true}
              value={documentFinishDate}
              onChange={setDocumentFinishDate}
            />
          </Form.Fieldset>
        </ModalRow>
        <Label>Posting Dates</Label>
        <ModalRow>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="sales-ledger-daybook-posting-start-date"
              label="Start"
              portal={true}
              value={postingStartDate}
              onChange={setPostingStartDate}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="sales-ledger-daybook-posting-finish-date"
              label="Finish"
              portal={true}
              value={postingFinishDate}
              onChange={setPostingFinishDate}
            />
          </Form.Fieldset>
        </ModalRow>
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="sales-ledger-daybook-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="sales-ledger-daybook-print" onClick={onPrint}>
          Print
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default SalesLedgerDayBookCriteriaModal;
